import { addDays, isBefore, startOfDay } from "date-fns";
import { v4 as uuid } from "uuid";
import StatefulSettings, { STATE_KEY_REPORTCOMPLETE } from "../controllers/StatefulSettings";
import {
  IEventItem,
  IRollResults,
  ITaskBasketInfo,
  ITaskTimerInfo,
  PenaltyBasketItem,
  RollState,
  IPenaltyTimerInfo,
  ISettingsItem,
  AuditMessage,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  PenaltyType,
  TraceLevel,
  EventType,
  TaskType,
} from "../ts/enums";
import { convertToEventDayFromDate } from "../utilities/dateUtils";
import { isAvailableItem } from "../utilities/utils";
import { sendDailyReport, sendExceptionMessage, NotificationType } from "../controllers/messaging";

/**
 * DayRollManager - Handles all day roll operations including:
 * - New day detection
 * - Start of day orchestration
 * - Daily checks (tasks, penalties, reports, positions)
 * - Bank charge calculations
 * - Missed day handling
 * - Daily state reset
 */
export class DayRollManager {
  constructor(
    private settings: StatefulSettings,
    private showMessage: (message: {
      message: string;
      fatal?: boolean;
      error?: boolean;
      notificationType: NotificationType;
    }) => void,
    private auditDecrement: (
      reason: string,
      desc: string,
      amount: number
    ) => void,
    private onTaskCompleted: (taskKey: string, checkComplete: boolean) => number,
    private triggerItemsAwaitingEvent: (eventKey: string, removeItFromSchedule: boolean) => void,
    private removeItemFromSchedule: (itemKey: string) => void,
    private checkForCompleteTaskVotes: (overrideSOD?: boolean) => void,
    private handlePendingItems: (override?: boolean, isDuringRoll?: boolean) => void,
    private handlePositionTick: () => void,
    private tidyTaskListOnRoll: () => IRollResults,
    private initialiseNewDay: (rollResults?: IRollResults) => Promise<void>,
    private onEventComplete: (
      event: IEventItem,
      isSystemTriggered: boolean,
      eventTriggerKey: string | undefined,
      ignoreMessage: boolean,
      scheduleItem: any
    ) => any
  ) { }

  /**
   * A completed SOD basket item from a previous day must not suppress today's
   * scheduled SOD. We only treat it as "already handled" when completion is today.
   */
  private isCompletedToday(basketItem: { isComplete?: boolean; completedTime?: Date | string } | undefined): boolean {
    if (!basketItem?.isComplete) return false;
    // Backward compatibility: some legacy persisted items may not include completedTime.
    // Preserve previous behavior for those by treating them as completed.
    if (!basketItem.completedTime) return true;
    const completed = new Date(basketItem.completedTime);
    if (Number.isNaN(completed.getTime())) return true;
    return !isBefore(completed, startOfDay(new Date()));
  }

  /**
   * Checks if it's a new day and triggers day roll if needed
   */
  public async checkIfNewDay(): Promise<boolean> {
    const dayDiff = this.settings.getRollDateDiff();
    if (dayDiff === 0) {
      const startOfDayEvent = this.settings.getStartOfDayEvent() as IEventItem;
      const isPending = this.settings.hasEventScheduled(startOfDayEvent.key);
      if (isPending) {
        // The SOD event is still in the schedule even though the control day is already
        // today. This can happen on a fresh page load that captured Firebase state
        // mid-roll (after rollControlDay() wrote today's date but before
        // removeItemFromSchedule() was persisted). If the day was already fully rolled
        // (basket item exists and is marked complete) the SOD entry is stale: remove it
        // now so checkScheduleForComplete does not re-fire onStartOfDay.
        const basketItem = this.settings.getTaskSchedule()[startOfDayEvent.key];
        if (this.isCompletedToday(basketItem)) {
          this.removeItemFromSchedule(startOfDayEvent.key);
        }
        return false;
      }

      //now check to see if SOD event has run already for today
      const basketItem = this.settings.getTaskSchedule()[startOfDayEvent.key];
      if (this.isCompletedToday(basketItem)) return false;
    }
    const rollState = await this.settings.rollDay();
    return rollState.didRoll;
  }

  /**
   * Main start of day orchestration
   */
  public async onStartOfDay(
    rollState: RollState,
    state: { value: string; setValue: (value: string) => void },
    startOfDayTime: { value: Date | undefined; setValue: (value: Date | undefined) => void }
  ): Promise<void> {
    try {
      if (state.value === "rollingDay") return; // means we are already consuming it so we don't need to so this second wave
      state.setValue("rollingDay");
      startOfDayTime.setValue(new Date());
      if (!rollState) rollState = { didRoll: false, audit: [] };
      rollState.audit = [];
      rollState.audit.push(import.meta.env.VITE_VERSION as string);
      this.settings.beginStateTransaction();
      await this.settings.rollControlDay(); // ensure when we do a roll, the control day is set today
      const id = uuid();
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Daily Roll Start",
          id,
          TraceLevel.info
        )
      );
      const sodEvent = this.settings.getStartOfDayEvent();
      if (sodEvent) {
        this.triggerItemsAwaitingEvent(sodEvent.key, false);
        this.removeItemFromSchedule(sodEvent.key);
      }
      // you want to do these in SOD routine before we then deal with items as we might not have finished items we thought we had
      // if things come in overnight
      this.checkForCompleteTaskVotes(true);
      this.handlePendingItems(true, true);
      let rollResults: IRollResults = {
        seedCredits: {},
        penalties: [],
        bankCharge: 0,
      };
      if (!this.settings.checkHolidayToday()) {
        const daysMissed = this.determineDaysMissed(rollState);
        this.handlePositionTick(); // tidies up any positions that were counting down when last logged on and not yet cleared up
        rollResults = this.tidyTaskListOnRoll();

        if (daysMissed > 0) {
          rollResults.penalties.push({
            reason: `Missed ${daysMissed} days`,
            points: daysMissed * 2,
          });
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.DailyChecks,
              "Penalty",
              "Missed Days Penalty",
              TraceLevel.info,
              false,
              daysMissed * 2
            )
          );
          const missedDayCharge =
            daysMissed * this.settings.getSessionSettings().dailyCost * 2;
          rollResults.bankCharge += missedDayCharge;
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.DailyChecks,
              "Bank",
              "Missed Days Charge",
              TraceLevel.info,
              false,
              missedDayCharge
            )
          );
        }
        if (rollResults.bankCharge < 0) {
          // they earnt money on the roll by completed items
          try {
            this.settings.incrementBank(
              rollResults.bankCharge,
              "Rolling Day"
            );
          } catch (error: unknown) {
            console.error("Failed to increment bank:", error);
          }
        } else {
          try {
            this.settings.decrementBank(
              rollResults.bankCharge,
              "Rolling Day"
            );
          } catch (error: unknown) {
            console.error("Failed to decrement bank:", error);
          }
        }
        const results = await sendDailyReport(
          this.settings,
          daysMissed,
          rollResults
        );
        this.settings.saveDailyScore(results.score, results.newPenaltyScore);
      } else {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "On Holiday Mode",
            "Not parsing current session today",
            TraceLevel.info
          )
        );
      }
      await this.initialiseNewDay(rollResults);
      // now pop in the sod event to the done list so we can prove it ran next time we want to check
      // could this be wiping out credits and events scheduled?
      if (sodEvent) {
        const eventBasketItem = this.onEventComplete(
          sodEvent,
          true,
          undefined,
          true, // ignoreMessage
          undefined
        );
        if (eventBasketItem) {
          // Get the tasks added for this event
          const schedule = this.settings.getTaskSchedule();
          const basketItems = Object.values(schedule).filter(t => t.key === sodEvent.key);
          // now set them to handled
          if (basketItems) {
            for (let i = 0; i < basketItems.length; i += 1) {
              this.onTaskCompleted(basketItems[i].key, false);
            }
          }
        }
      }
      startOfDayTime.setValue(undefined);
      this.settings.endStateTransaction();
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Daily Roll End",
          id,
          TraceLevel.info
        )
      );
    } catch (error) {
      const e = error instanceof Error ? error : new Error(String(error));
      sendExceptionMessage(this.settings, e);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Exception,
          "Daily Roll Error",
          e.message,
          TraceLevel.error
        )
      );
      this.showMessage({
        message: "Set up error. Contact Admin",
        fatal: true,
        notificationType: NotificationType.ErrorInfo,
      });
    }
  }

  /**
   * Determines how many days were missed based on roll state
   */
  public determineDaysMissed(rollState: RollState): number {
    if (!rollState || !rollState.didRoll) return 0;
    // if days is 1, this is fine, this means it is the next day
    if (!rollState.days || rollState.days <= 1) return 0;

    const unavailableDays = this.settings.getUnavailableDays();
    // deduct weekend days if the user has skip weekend enabled
    if (unavailableDays && unavailableDays.length > 0) {
      const daysToCount = rollState.days < 5 ? rollState.days : 5;
      for (let i = 1; i <= daysToCount; i++) {
        if (
          unavailableDays.indexOf(
            convertToEventDayFromDate(addDays(new Date(), i * -1).getDay())
          ) > -1
        )
          rollState.days -= 1;
      }
    }
    if (!rollState.days || rollState.days <= 1) return 0;
    return rollState.days < 5 ? rollState.days : 5;
  }

  /**
   * Checks for report completion during daily roll
   */
  public checkReportCompletion(
    penalties: { reason: string; points: number }[]
  ): number {
    let bankCharge = 0;
    const generalSettings = this.settings.getGeneralSettings();
    const reportComplete = this.settings.getState(
      STATE_KEY_REPORTCOMPLETE,
      false
    ); // if not set up yet, then assume not required
    if (generalSettings.dailyReporting && !reportComplete) {
      const cost = generalSettings.dailyReportingCredit;
      bankCharge += cost;
      this.auditDecrement("Report Missed", "Daily Checks", cost);
      this.showMessage({
        message: "Penalty applied for failing to complete Daily Report",
        error: true,
        notificationType: NotificationType.ErrorInfo,
      });
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Penalty,
          "Missed Daily Report",
          "Uncompleted Penalty Accrued",
          TraceLevel.warning
        )
      );
      this.addDailyRollPenalty(penalties, "Missed Daily Report", 1);
    }
    return bankCharge;
  }

  /**
   * Checks penalty completion during daily roll
   */
  public checkPenaltyCompletion(
    seedCredits: ITaskBasketInfo,
    penalties: { reason: string; points: number }[]
  ): number {
    const penaltiesToReapply: (ITaskTimerInfo | IPenaltyTimerInfo)[] = [];
    let bankCharge = 0;
    const creditInfo = this.settings.getTaskSchedule();
    const unfinishedPenalties = Object.keys(creditInfo).filter(
      (c) =>
        !creditInfo[c].isComplete &&
        creditInfo[c].isPenalty &&
        isAvailableItem(this.settings.getPenalty(c) as ISettingsItem) &&
        (creditInfo[c] as PenaltyBasketItem).penaltyType !==
        PenaltyType.acknowledgement // acknowledgements can be ignored
    );
    unfinishedPenalties.forEach((penaltyKey) => {
      const item = creditInfo[penaltyKey];
      const displayName = item.name;
      const awaitingStartOfDay =
        item.endTriggerEvent &&
        this.settings.getEvent(item.endTriggerEvent)?.eventType ===
        EventType.startOfDay;
      if (!awaitingStartOfDay && this.settings.shouldTaskRoll(item, false)) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Penalty,
            item.name,
            "Trigger Event not met - Rolling over Task to new day",
            TraceLevel.warning
          )
        );
        seedCredits[item.key] = item; // this allows use to continue with a credit which we are still awaiting an event for
        return;
      }
      const isOk =
        awaitingStartOfDay || this.settings.hasTaskFinished(item, false, true);

      if (isOk) {
        // handle this has finished before midnight and acceptable
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.DailyChecks,
            displayName,
            "Item Completed",
            TraceLevel.info
          )
        );
        this.showMessage({
          message: `Penalty Item Completed - ${displayName}`,
          notificationType: NotificationType.ScheduleInfo,
        });
      } else {
        item.isPaused = true; // reset it to start again, it was started before the new day and didn't complete by the end of the day
        // roll over penalties to the next day with current settings
        penaltiesToReapply.push(item);
        const bank = this.settings.getBankBalance();
        bankCharge += bank * 0.5;
        this.auditDecrement("Penalty Item Unfinished", displayName, bank * 0.5);
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Penalty,
            displayName,
            "Uncompleted Penalty Accrued",
            TraceLevel.warning
          )
        );
        this.addDailyRollPenalty(
          penalties,
          "Unfinished Penalty " + displayName,
          1
        );
      }
    });

    if (penaltiesToReapply.length > 0) {
      penaltiesToReapply.forEach((penalty) => {
        seedCredits[penalty.key] = penalty; // this allows use to continue with a penalty which is already running and keep the timer going
      });
    }
    return bankCharge;
  }

  /**
   * Checks task completion during daily roll
   */
  public checkForTasksCompletion(
    seedCredits: ITaskBasketInfo,
    penalties: { reason: string; points: number }[]
  ): number {
    let bankCharge = 0;
    const tasks = this.settings.getTasks();
    const taskSchedule = this.settings.getTaskSchedule();
    const unfinishedCredits = Object.keys(taskSchedule).filter(
      (c) =>
        !(taskSchedule[c] as ITaskTimerInfo).permanent &&
        !taskSchedule[c].isComplete &&
        !taskSchedule[c].isPenalty &&
        (taskSchedule[c] as ITaskTimerInfo).creditTaskType !==
        TaskType.acknowledgement && // acknowledgements can be ignored
        isAvailableItem(this.settings.getTask(c) as ISettingsItem)
    );
    for (let i = 0; i < unfinishedCredits.length; i += 1) {
      const creditMissedKey = unfinishedCredits[i];
      const item = taskSchedule[creditMissedKey];
      const task = tasks.find((c) => c.key === creditMissedKey) || item;
      if (!task) continue; // means an item that did exist no longer exists - deleted most probably
      const displayName = task.name;
      const awaitingStartOfDay =
        item.endTriggerEvent &&
        this.settings.getEvent(item.endTriggerEvent)?.eventType ===
        EventType.startOfDay;
      if (
        !item.isPaused && // no credit that is paused shuld be considered for rolls
        !awaitingStartOfDay &&
        this.settings.shouldTaskRoll(item, false)
      ) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.DailyChecks,
            item.name,
            "Rolling over Task to new day",
            TraceLevel.info
          )
        );
        seedCredits[item.key] = item; // this allows use to continue with a credit which we are still awaiting an event for
        continue;
      }
      const isOk =
        awaitingStartOfDay || this.settings.hasTaskFinished(item, false, true);
      if (isOk) {
        const reward = this.onTaskCompleted(creditMissedKey, false);
        bankCharge -= reward;
      } else if ((task as ITaskTimerInfo).credit) {
        bankCharge += (task as ITaskTimerInfo).credit;
        this.auditDecrement(
          "Credit Unfinished",
          displayName,
          (task as ITaskTimerInfo).credit
        );
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Penalty,
            displayName,
            "Uncompleted Penalty Accrued",
            TraceLevel.warning
          )
        );
        this.addDailyRollPenalty(
          penalties,
          "Unfinished Task " + displayName,
          task.timesRequired ?? 1
        );
      }
    }
    return bankCharge;
  }

  /**
   * Handles owner tax charges during daily roll
   */
  public handleOwnerTax(): number {
    let bankCharge = 0;
    if (!this.settings.canPerformSomeTasksForToday()) return bankCharge;
    const generalSettings = this.settings.getGeneralSettings();
    const { dailyCost } = generalSettings;
    const bankBalance = this.settings.getBankBalance();
    if (bankBalance > 0) {
      if (dailyCost > bankBalance) this.settings.payOwner(bankBalance);
      else this.settings.payOwner(dailyCost);
    }
    bankCharge += dailyCost;
    this.auditDecrement("Owner Tax", "Daily Checks", dailyCost);
    return bankCharge;
  }

  /**
   * Adds a penalty to the daily roll penalty list
   */
  private addDailyRollPenalty(
    penalties: { reason: string; points: number }[],
    reason: string,
    points: number
  ): void {
    penalties.push({ reason, points });
  }
}
